import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Audience,
  audienceOf,
  getLearnTracks,
  getQuizContent,
  LearnTracksPayload,
  putLessonAudience,
  putTrackAudience,
  QuizContent,
  QuizQuestion,
} from '../../services/quizContentAdminService';
import {
  getTenantTranslationLanguages,
  languageLabel,
} from '../../services/translationsAdminService';
import { useTenant } from '../../contexts/TenantContext';
import { useOptionalAuth } from '../../contexts/AuthContext';
import ScorecardNavigation from '../restaurant/ScorecardNavigation';
import QuizReviewLanguageControl, {
  QUIZ_REVIEW_LANGUAGE_COPY,
} from './QuizReviewLanguageControl';
import {
  apiLangFor,
  LANG_PARAM,
  LanguageSelection,
  langParamFor,
  nextMode,
  parseLangParam,
} from './quizReviewLanguage';
import { QUIZ_REVIEW_NAV_COPY as COPY } from './quizReview/navCopy';
import { groupByTrack } from './quizReview/groupByTrack';
import { findNavTarget, indexSections } from './quizReview/reviewKeys';
import { ReviewToolbar } from './quizReview/ReviewToolbar';
import {
  ContentsPill,
  ReviewTocPanel,
  ReviewTocPopover,
  ReviewTocSheet,
  useTocExpansion,
} from './quizReview/ReviewToc';
import { buildToc, tocPathTo } from './quizReview/tocModel';
import { useScrollSpy } from './quizReview/useScrollSpy';
import {
  Layout,
  ReviewBody,
  SectionControls,
  SectionControlsContext,
} from './quizReview/ReviewBody';
import { EditCoordinatorProvider } from './quizReview/editCoordinator';
import { SrOpenHint } from './quizReview/InlineEditable';
import { usePendingApprovals } from './quizReview/pendingApprovals';
import { ReviewEditContext, useReviewEditApi } from './quizReview/reviewEdit';
import { PendingNote, SubtitleEditHint } from './quizReview/ReviewEditChrome';
import { QuestionEditProvider } from './quizReview/voiceEdit/QuestionEditProvider';
import { NeedsYouCounter } from './quizReview/voiceEdit/NeedsYouCounter';
import { questionPlacements } from './quizReview/voiceEdit/placements';
import { questionPatchFrom } from './quizReview/contentPatch';
import {
  AudienceApi,
  AudienceProvider,
  OptionsState,
  readyOptions,
  TrackRef,
} from './quizReview/audience/audienceContext';
import { useViewAs } from './quizReview/audience/useViewAs';
import { filterGrouped, isEmptyView } from './quizReview/audience/viewAsFilter';
import {
  ViewAsBanner,
  ViewAsControls,
  ViewAsEmpty,
  ViewAsTocLine,
} from './quizReview/audience/ViewAs';
import { AUDIENCE_COPY } from './quizReview/audience/audienceCopy';
import { AudienceLevel } from './quizReview/audience/audienceCopy';

/**
 * The tenant's configured non-English languages.
 *
 * `null` = not known yet (the GET has not resolved, or it failed). `[]` = the
 * tenant genuinely translates into nothing. Both render NO control, but they are
 * kept distinct because they are different facts and only one of them is a
 * problem — `getTenantTranslationLanguages` throws rather than returning an empty
 * shape precisely so this distinction survives, and a deploy-skew 404 has to be
 * diagnosable from the console (feat-260, translationsAdminService.ts:253).
 */
type TenantLanguages = string[] | null;

/**
 * feat-298 — the tenant's Tracks. `'loading'` until the GET settles; `null` when
 * it failed, which renders the flat (no-Track) fallback with no error UI.
 */
type TenantTracks = LearnTracksPayload | null | 'loading';

/**
 * feat-298 — how long the body waits for Tracks once the curriculum is in hand.
 * Past this the page renders the flat fallback and a late answer is ignored, so a
 * request that hangs never leaves the page on "Loading" and a late arrival never
 * re-lays-out the page under the reader (QA L2).
 */
export const TRACKS_WAIT_MS = 8000;

/**
 * The content container's width and grid (design NOTES §0).
 *
 * Not docked ⇒ EXACTLY today's classes, so hiding the TOC returns the page to its
 * feat-284 geometry. Docked ⇒ a 272 px TOC column beside the content: from `lg`
 * in EN/ES, but only from `xl` in Both, where docking at 1024 would leave about
 * 300 px per language. Only Both widens; EN/ES keep a `max-w-4xl` reading column.
 */
function containerClasses(both: boolean, docked: boolean): string {
  if (!docked) return both ? 'max-w-[1700px]' : 'max-w-4xl';
  return both
    ? 'max-w-[1700px] xl:max-w-[2004px] xl:grid xl:grid-cols-[272px_minmax(0,1fr)] xl:gap-x-8 xl:items-start'
    : 'max-w-4xl lg:max-w-[1264px] lg:grid lg:grid-cols-[272px_minmax(0,1fr)] lg:gap-x-8 lg:items-start';
}

const QuizContentReview: React.FC = () => {
  const { selectedTenant, tenantName } = useTenant();
  const [content, setContent] = useState<QuizContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [languages, setLanguages] = useState<TenantLanguages>(null);
  const [tracks, setTracks] = useState<TenantTracks>('loading');
  const [searchParams, setSearchParams] = useSearchParams();
  const contentRef = useRef<HTMLDivElement | null>(null);
  // feat-299 — exact pending markers (spec §5), and the inline-edit writers.
  // Keyed on tenant only, never on language: EN/ES/Both fire no request.
  const pending = usePendingApprovals(selectedTenant);
  /** feat-300 R2 3c: the AI-edit layer registers here to hear about inline
   *  question saves (an open proposal may have just gone stale). */
  const questionSavedRef = useRef<((id: string) => void) | null>(null);
  const editApi = useReviewEditApi(setContent, pending, {
    onQuestionSaved: (id) => questionSavedRef.current?.(id),
  });
  /** The item the reader was looking at when a mode change started, and how far
   *  below the nav it sat. Consumed once, by the layout effect below. */
  const scrollAnchorRef = useRef<{ key: string; delta: number } | null>(null);

  /**
   * The y of the first pixel the reader can actually see.
   *
   * ScorecardNavigation is `sticky top-0` with a VARIABLE height — the welcome
   * row wraps — so its height is measured, never hardcoded. It is this content
   * container's previous sibling, which is a local, stable relationship that does
   * not require touching ScorecardNavigation.tsx.
   */
  const navBottom = useCallback((): number => {
    const nav = contentRef.current?.previousElementSibling as HTMLElement | null;
    const bottom = nav?.getBoundingClientRect().bottom ?? 0;
    return bottom > 0 ? bottom : 0;
  }, []);

  /**
   * Record the top-most curriculum item intersecting the viewport, BEFORE the
   * mode changes.
   *
   * The anchor is the ITEM, not a pixel offset, which is what lets it survive the
   * single↔two-column relayout — the case a pixel offset would break outright.
   * Every item carries a mode-independent `data-item-key` (see ItemCells), and in
   * Both mode it lives on the English cell, so EN → Both → ES all anchor to the
   * same element.
   */
  const captureScrollAnchor = useCallback(() => {
    const top = navBottom();
    const nodes = contentRef.current?.querySelectorAll<HTMLElement>('[data-item-key]');
    if (!nodes) return;
    for (let i = 0; i < nodes.length; i += 1) {
      const rect = nodes[i].getBoundingClientRect();
      if (rect.top >= top) {
        const key = nodes[i].getAttribute('data-item-key');
        if (key) scrollAnchorRef.current = { key, delta: rect.top - top };
        return;
      }
    }
    scrollAnchorRef.current = null;
  }, [navBottom]);

  /**
   * Put that item back under the reader's eye after the DOM has changed.
   *
   * `useLayoutEffect`, NOT `useEffect`: `useEffect` paints at the old scroll
   * position first and the reader sees the page jump and snap back.
   * `behavior: 'auto'`, never `'smooth'` — this is a correction, not a journey.
   *
   * A missing key leaves the scroll where it is rather than jumping to the top.
   * No item should disappear between modes, but "do nothing" is the strictly
   * better failure.
   */
  useLayoutEffect(() => {
    const anchor = scrollAnchorRef.current;
    if (!anchor) return;
    scrollAnchorRef.current = null;
    const nodes = contentRef.current?.querySelectorAll<HTMLElement>('[data-item-key]');
    if (!nodes) return;
    let target: HTMLElement | null = null;
    for (let i = 0; i < nodes.length; i += 1) {
      if (nodes[i].getAttribute('data-item-key') === anchor.key) {
        target = nodes[i];
        break;
      }
    }
    if (!target) return;
    const top = navBottom();
    const rect = target.getBoundingClientRect();
    window.scrollTo({ top: window.scrollY + rect.top - top - anchor.delta, behavior: 'auto' });
  });

  // URL is the source of truth for the mode, so `?lang=es` is Spanish on FIRST
  // PAINT — there is no English-then-flip because there is no mode state to
  // initialise late. `languages` gates it: an unrecognised or not-yet-known
  // language falls back to EN without throwing (spec §2).
  const selection = parseLangParam(searchParams.get(LANG_PARAM), languages ?? []);
  const { mode } = selection;

  /**
   * "Latest value" refs, read by the `l` listener and by `setSelection`.
   *
   * NOT a micro-optimisation — this closes a real bug, and it is worth stating
   * because the shape recurs. `selection` and `searchParams` change on every
   * render, so a handler closing over them directly had to be torn down and
   * re-registered by a passive effect after each one. Passive effects run AFTER
   * the commit, so there is a window where the DOM already shows the new mode
   * but the live listener is still the previous one, holding the previous mode.
   * A second `l` landing in that window cycled from the STALE mode: EN → ES,
   * then EN → ES again instead of Both. The full jest run hit it about one run
   * in four; a reader drumming the key would hit it in a browser.
   *
   * `setSelection` ALSO advances `selectionRef` itself, rather than waiting for
   * the URL round-trip to come back through render. Without that, two presses
   * dispatched before ANY render — one React batch — would still both read the
   * pre-press mode. The render-phase assignment below then reconciles the ref
   * with what the URL actually says, so an optimistic value can never persist.
   *
   * `setSelection` itself is behind a ref too (`setSelectionRef`, just below it),
   * for a reason that is not obvious:
   * react-router's own `setSearchParams` lists `searchParams` in its dependency
   * array (`react-router-dom/dist/index.js`, `useSearchParams`), so its identity
   * CHANGES ON EVERY NAVIGATION. Anything deriving from it — including this
   * file's `setSelection` — is therefore unstable by construction, and an effect
   * depending on it re-registers after every mode change. That is the same
   * commit-then-effect window again: a key pressed while it is being re-armed
   * lands on nothing at all. Routing it through a ref lets the listener attach
   * exactly once.
   *
   * Written during render (the standard "latest ref" pattern). This page uses no
   * concurrent features, so there is no discarded-render hazard to guard against.
   */
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const setSelection = useCallback(
    (next: LanguageSelection) => {
      selectionRef.current = next;
      captureScrollAnchor();
      const value = langParamFor(next);
      const params = new URLSearchParams(searchParamsRef.current);
      if (value === null) params.delete(LANG_PARAM);
      else params.set(LANG_PARAM, value);
      // `replace`, not push: a reviewer who flips EN↔ES a dozen times while
      // reading must not have to Back through every one of them.
      setSearchParams(params, { replace: true });
    },
    [setSearchParams, captureScrollAnchor],
  );
  const setSelectionRef = useRef(setSelection);
  setSelectionRef.current = setSelection;

  // --- the tenant's language list -------------------------------------------
  useEffect(() => {
    let cancelled = false;
    if (!selectedTenant) {
      setLanguages(null);
      return undefined;
    }
    setLanguages(null);
    (async () => {
      try {
        const data = await getTenantTranslationLanguages(selectedTenant);
        if (cancelled) return;
        setLanguages((data.translation_languages ?? []).filter((c) => c && c !== 'en'));
      } catch (e) {
        if (cancelled) return;
        // Degrade to English-only with no control rather than breaking the page
        // (slice brief §5). The page itself is unaffected — this is a side fetch,
        // the same shape as the Revel fetch in ShiftTranscriptView.
        // eslint-disable-next-line no-console
        console.error('[QuizContentReview] GET translation-languages failed:', e);
        setLanguages([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTenant]);

  // --- the tenant's Tracks (feat-298) ----------------------------------------
  //
  // Set when the wait below gave up on this tenant's request; its late answer
  // (success OR failure) is then dropped.
  const tracksTimedOutRef = useRef(false);
  // Keyed on TENANT ONLY, never on language or mode: Track titles are English
  // structural chrome, and EN/ES/Both must still fire no request (feat-284 AC4).
  // Same degrade-don't-break shape as the translation-languages side fetch above.
  useEffect(() => {
    let cancelled = false;
    if (!selectedTenant) {
      setTracks(null);
      return undefined;
    }
    setTracks('loading');
    tracksTimedOutRef.current = false;
    (async () => {
      try {
        const data = await getLearnTracks(selectedTenant);
        if (cancelled || tracksTimedOutRef.current) return;
        setTracks(data);
      } catch (e) {
        if (cancelled || tracksTimedOutRef.current) return;
        // eslint-disable-next-line no-console
        console.error('[QuizContentReview] GET learn-tracks failed:', e);
        setTracks(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTenant]);

  // --- the curriculum -------------------------------------------------------
  //
  // Keyed on tenant + LANGUAGE, never on mode. EN→ES→Both must fire no network
  // request at all (AC4), which is only possible if the overlay is already in
  // hand before the reviewer clicks. Gated on `languages !== null` so the first
  // request already carries `lang`; the alternative — fetch now, refetch when the
  // list arrives — is two requests for every page load and fails AC4 outright.
  const fetchLanguage = languages === null ? null : apiLangFor(selection) ?? '';

  const reload = useCallback(async () => {
    if (!selectedTenant || fetchLanguage === null) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getQuizContent(selectedTenant, fetchLanguage || undefined);
      setContent(data);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, fetchLanguage]);

  useEffect(() => {
    if (selectedTenant) reload();
    else setContent(null);
  }, [selectedTenant, reload]);

  // The Tracks GET is a side fetch the page degrades around, so it never holds
  // the body hostage: TRACKS_WAIT_MS after the curriculum is ready, a request
  // still pending is abandoned and the flat fallback renders (logged once).
  const contentReady = !!content;
  const tracksPending = tracks === 'loading';
  useEffect(() => {
    if (!selectedTenant || !contentReady || !tracksPending) return undefined;
    const timer = window.setTimeout(() => {
      tracksTimedOutRef.current = true;
      // eslint-disable-next-line no-console
      console.error(
        `[QuizContentReview] GET learn-tracks did not settle within ${TRACKS_WAIT_MS} ms; rendering without Tracks`,
      );
      setTracks(null);
    }, TRACKS_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [selectedTenant, contentReady, tracksPending]);

  // --- feat-302: audience scope --------------------------------------------------
  //
  // The option lists ride on the learn-tracks payload (ADR §10.2). A retry from
  // the editor re-reads learn-tracks but takes ONLY `audience_options` from it —
  // never re-lays-out the page from a late Tracks answer (feat-298 QA L2).
  const [optionsRetry, setOptionsRetry] = useState<OptionsState | null>(null);
  useEffect(() => {
    setOptionsRetry(null);
  }, [selectedTenant]);
  const baseOptions = useMemo<OptionsState>(
    () =>
      tracks === 'loading'
        ? { status: 'loading' }
        : tracks && tracks.audience_options
          ? { status: 'ready', options: tracks.audience_options }
          : { status: 'failed' },
    [tracks],
  );
  const audienceOptions = optionsRetry ?? baseOptions;
  const retryOptions = useCallback(() => {
    if (!selectedTenant) return;
    setOptionsRetry({ status: 'loading' });
    getLearnTracks(selectedTenant)
      .then((data) =>
        setOptionsRetry(
          data.audience_options
            ? { status: 'ready', options: data.audience_options }
            : { status: 'failed' },
        ),
      )
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('[QuizContentReview] GET learn-tracks (audience options) failed:', String(e));
        setOptionsRetry({ status: 'failed' });
      });
  }, [selectedTenant]);

  const tracksRef = useRef(tracks);
  tracksRef.current = tracks;
  const tracksForLesson = useCallback((lessonId: string): TrackRef[] => {
    const t = tracksRef.current;
    if (!t || t === 'loading') return [];
    return t.tracks
      .filter((tr) => (tr.lesson_ids ?? []).includes(lessonId))
      .map((tr) => ({ id: tr.id, title: tr.title, audience: audienceOf(tr) }));
  }, []);

  /** Set by View as (below): called after a successful audience save. */
  const afterAudienceSaveRef = useRef<((level: AudienceLevel, id: string) => void) | null>(null);

  /** Write, then patch the in-memory tree with the server's canonical object —
   *  no full refetch (the feat-299 pattern). */
  const saveAudience = useCallback(
    async (level: AudienceLevel, id: string, audience: Audience): Promise<void> => {
      if (level === 'track') {
        const stored = await putTrackAudience(id, audience);
        setTracks((prev) =>
          prev && prev !== 'loading'
            ? {
                ...prev,
                tracks: prev.tracks.map((t) => (t.id === id ? { ...t, audience: stored } : t)),
              }
            : prev,
        );
      } else {
        const stored = await putLessonAudience(id, audience);
        setContent((prev) =>
          prev
            ? {
                ...prev,
                lessons: prev.lessons.map((u) => (u.id === id ? { ...u, audience: stored } : u)),
              }
            : prev,
        );
      }
      afterAudienceSaveRef.current?.(level, id);
    },
    [],
  );

  // --- feat-302: View as ---------------------------------------------------------
  const viewAs = useViewAs({
    tenantId: selectedTenant,
    searchParams,
    setSearchParams,
    options: readyOptions(audienceOptions),
  });
  afterAudienceSaveRef.current = (level, id) => {
    const t = tracksRef.current;
    const lessonIds =
      level === 'track' && t && t !== 'loading'
        ? t.tracks.find((tr) => tr.id === id)?.lesson_ids ?? []
        : undefined;
    viewAs.afterSave(level, id, lessonIds);
  };

  const audienceApi = useMemo<AudienceApi>(
    () => ({
      options: audienceOptions,
      retryOptions,
      tracksForLesson,
      save: saveAudience,
      editedOutLine: viewAs.editedOutLine,
      viewName: viewAs.viewName,
    }),
    [audienceOptions, retryOptions, tracksForLesson, saveAudience, viewAs.editedOutLine, viewAs.viewName],
  );


  const tracksReady = tracks !== 'loading';
  /** The whole curriculum, grouped (feat-298). */
  const fullGrouped = useMemo(
    () => (content && tracksReady ? groupByTrack(content.lessons, tracks) : null),
    [content, tracks, tracksReady],
  );
  /** What renders: the whole tree, or — under View as — the tree filtered to the
   *  backend's id sets plus the pinned just-edited row (design Q3). Everything
   *  downstream (body, TOC, sections, AI-edit placements) reads THIS. */
  const viewApplied = viewAs.applied;
  const { pinnedTrackIds, pinnedLessonIds } = viewAs;
  const grouped = useMemo(() => {
    if (!fullGrouped || !viewApplied) return fullGrouped;
    return filterGrouped(fullGrouped, {
      trackIds: new Set([...viewApplied.track_ids, ...Array.from(pinnedTrackIds)]),
      lessonIds: new Set([...viewApplied.lesson_ids, ...Array.from(pinnedLessonIds)]),
    });
  }, [fullGrouped, viewApplied, pinnedTrackIds, pinnedLessonIds]);
  /** Banner counts — display counts over the ids the backend returned. */
  const viewCounts = useMemo(() => {
    if (!viewApplied || !content || !fullGrouped) return null;
    const trackIds = new Set(viewApplied.track_ids);
    const lessonIds = new Set(viewApplied.lesson_ids);
    const tracksAll = fullGrouped.mode === 'tracks' ? fullGrouped.groups.filter((g) => g.kind === 'track') : [];
    return {
      t: tracksAll.filter((g) => trackIds.has(g.id)).length,
      tt: tracksAll.length,
      l: content.lessons.filter((u) => lessonIds.has(u.id)).length,
      ll: content.lessons.length,
    };
  }, [viewApplied, content, fullGrouped]);
  const viewEmpty = viewAs.active && !!viewApplied && !!grouped && isEmptyView(grouped);

  const sections = useMemo(() => (grouped ? indexSections(grouped) : null), [grouped]);

  // --- feat-300: AI edit threads ----------------------------------------------
  const auth = useOptionalAuth();
  const placements = useMemo(() => questionPlacements(grouped), [grouped]);
  const placementsRef = useRef(placements);
  placementsRef.current = placements;
  const editApiRef = useRef(editApi);
  editApiRef.current = editApi;
  /** Apply's 200 carries the updated question. It goes through feat-299's ONE
   *  patch path (`contentPatch.patchQuestion` via `questionWritten`), so the card
   *  re-renders with no refetch (AT-16), and that Lesson's pending set is re-read
   *  and believed — a no-op Apply shows no chip (R2 3a/3b). */
  const onQuestionApplied = useCallback((question: QuizQuestion) => {
    const lessonId =
      placementsRef.current.find((p) => p.questionId === question.id)?.lessonId ?? null;
    editApiRef.current.questionWritten(lessonId, question.id, questionPatchFrom(question));
  }, []);
  /** Rendered with something to navigate: the toolbar and TOC are hidden while
   *  loading, on error, with no tenant, and for an empty curriculum (NOTES §12). */
  const navAvailable =
    !!content && !!grouped && !loading && !error && content.lessons.length > 0;

  // --- collapse state (spec §4) ---------------------------------------------
  //
  // The set of COLLAPSED section keys; empty = everything expanded, the default.
  // Lives here, keyed by stable ids, so it survives EN/ES/Both switches (and the
  // `l` hotkey) untouched. Reset on tenant change. Never written to the URL.
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    setCollapsed(new Set());
  }, [selectedTenant]);

  const toggleSection = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const expandAll = useCallback(() => setCollapsed(new Set()), []);
  const collapseAll = useCallback(() => {
    if (sections) setCollapsed(new Set(sections.belowTrack));
  }, [sections]);

  // --- the sticky-chrome height (design NOTES §1) ------------------------------
  //
  // ScorecardNavigation's height VARIES (the welcome row wraps), so it is
  // measured, never hard-coded, and shared as `--review-chrome-top`: the TOC's
  // sticky `top`, every jump target's `scroll-margin-top`, and the spy's line.
  const [chromeTop, setChromeTop] = useState(86);
  useEffect(() => {
    const nav = contentRef.current?.previousElementSibling as HTMLElement | null;
    if (!nav || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      const h = nav.getBoundingClientRect().height;
      if (h > 0) setChromeTop(Math.round(h));
    });
    ro.observe(nav);
    return () => ro.disconnect();
  }, []);

  // --- scroll-spy + TOC state (spec §3) ---------------------------------------
  const spyVersion = useMemo(() => ({}), [collapsed, grouped, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const spy = useScrollSpy(contentRef, chromeTop + 16, navAvailable, spyVersion);
  const beginJumpRef = useRef(spy.beginJump);
  beginJumpRef.current = spy.beginJump;
  const toc = useMemo(() => (grouped ? buildToc(grouped, COPY.unassignedTitle) : []), [grouped]);
  const activePath = useMemo(() => tocPathTo(toc, spy.activeKey), [toc, spy.activeKey]);
  const tocExpansion = useTocExpansion(activePath, selectedTenant);
  /** The reader clicked "Hide contents": back to today's page geometry. */
  const [tocHidden, setTocHidden] = useState(false);
  const [tocOverlay, setTocOverlay] = useState<null | 'sheet' | 'popover'>(null);
  const pillRef = useRef<HTMLButtonElement | null>(null);

  // --- jumping to a section (stub rows, TOC) ----------------------------------
  //
  // Expand the target and its ancestors FIRST, then scroll after that commit:
  // a collapsed target is not in the DOM until it is expanded.
  // `scroll-margin-top` on every target keeps it clear of the sticky nav.
  const pendingJumpRef = useRef<string | null>(null);
  const [jumpSeq, setJumpSeq] = useState(0);
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  const jumpTo = useCallback((key: string) => {
    const expand = [...(sectionsRef.current?.ancestors.get(key) ?? []), key];
    setCollapsed((prev) => {
      if (!expand.some((k) => prev.has(k))) return prev;
      const next = new Set(prev);
      expand.forEach((k) => next.delete(k));
      return next;
    });
    pendingJumpRef.current = key;
    setJumpSeq((n) => n + 1);
    beginJumpRef.current(key);
  }, []);

  useLayoutEffect(() => {
    const key = pendingJumpRef.current;
    if (!key) return;
    pendingJumpRef.current = null;
    const el = findNavTarget(contentRef.current, key);
    if (!el) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    el.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
    // Land keyboard and screen-reader users where they jumped.
    el.querySelector<HTMLElement>('[data-nav-heading]')?.focus({ preventScroll: true });
  }, [jumpSeq]);

  // --- feat-300: revealing one ITEM (the AI-edit counter's jump) ---------------
  //
  // `jumpTo`'s expansion half, then scroll the item itself — not its Drill
  // banner — into view after the commit. No focus move: the caller focuses the
  // item's thread panel. Additive; `jumpTo` is unchanged.
  const pendingRevealRef = useRef<string | null>(null);
  const [revealSeq, setRevealSeq] = useState(0);
  const revealItem = useCallback((drill: string, itemKey: string) => {
    const expand = [...(sectionsRef.current?.ancestors.get(drill) ?? []), drill];
    setCollapsed((prev) => {
      if (!expand.some((k) => prev.has(k))) return prev;
      const next = new Set(prev);
      expand.forEach((k) => next.delete(k));
      return next;
    });
    pendingRevealRef.current = itemKey;
    setRevealSeq((n) => n + 1);
    beginJumpRef.current(drill);
  }, []);

  useLayoutEffect(() => {
    const key = pendingRevealRef.current;
    if (!key) return;
    pendingRevealRef.current = null;
    const nodes = contentRef.current?.querySelectorAll<HTMLElement>('[data-item-key]');
    if (!nodes) return;
    for (let i = 0; i < nodes.length; i += 1) {
      if (nodes[i].getAttribute('data-item-key') === key) {
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        nodes[i].scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
        return;
      }
    }
  }, [revealSeq]);

  const sectionControls = useMemo<SectionControls>(
    () => ({ isOpen: (k) => !collapsed.has(k), toggle: toggleSection, jumpTo, revealItem }),
    [collapsed, toggleSection, jumpTo, revealItem],
  );

  const matches = (query: string): boolean =>
    typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  const both = mode === 'both';
  const docked = navAvailable && !tocHidden;

  const onContentsPill = () => {
    if (tocOverlay) {
      setTocOverlay(null);
      return;
    }
    // Where the panel CAN dock, the pill (shown only because it was hidden)
    // re-docks it; elsewhere it opens the sheet, or the popover in Both lg–xl.
    if (tocHidden && matches(both ? '(min-width: 1280px)' : '(min-width: 1024px)')) {
      setTocHidden(false);
      return;
    }
    setTocOverlay(both && matches('(min-width: 1024px)') ? 'popover' : 'sheet');
  };
  /** Dismissed without choosing: focus goes back to the pill. */
  const dismissOverlay = useCallback(() => {
    setTocOverlay(null);
    pillRef.current?.focus();
  }, []);
  /** Close FIRST, then jump — releases the sheet's scroll lock before scrolling. */
  const jumpFromOverlay = useCallback(
    (key: string) => {
      setTocOverlay(null);
      jumpTo(key);
    },
    [jumpTo],
  );

  // --- the `l` hotkey -------------------------------------------------------
  const controlVisible = !!content && !loading && !error && !!languages && languages.length > 0;

  /** Mode → layout. EN and ES are both single-column and differ only in which
   *  language the authored text is in; only Both changes the shape of the page. */
  const layout: Layout = {
    mode: mode === 'both' ? 'both' : 'single',
    column: mode === 'en' ? null : selection.language || null,
  };

  /**
   * Visibility goes through a ref for the same reason everything else here does,
   * and this one was found by QA rather than by me — see the AC-F5 section of
   * `reviews/qa-feat-284-curriculum-review-languages.md`.
   *
   * The effect below used to read `if (!controlVisible) return undefined;` with
   * `[controlVisible]` as its dependency. That is the ordinary way to write it
   * and it is wrong here, because passive effects run AFTER the commit: the
   * curriculum paints, the control appears, and the listener attaches **one
   * flush later**. QA instrumented the gap directly —
   *
   *     DIAG after load: added=0            content rendered, control visible
   *     DIAG after extra flush 1: added=1   the listener attaches only now
   *
   * — and a press inside that window is not deferred, it is **lost**, with no
   * feedback to the reader. The window reopens on every `controlVisible`
   * false→true transition, so it is not only page load: a tenant switch and a
   * language switch each reopen it, which is when a reviewer is most likely to
   * be reaching for the key.
   *
   * Attaching once on mount with `[]` and testing visibility INSIDE the handler
   * removes the window entirely — there is no moment after mount when the
   * listener is absent. The behaviour is unchanged: a press while the control is
   * hidden still does nothing, it is just decided at press time instead of at
   * attach time.
   */
  const controlVisibleRef = useRef(controlVisible);
  controlVisibleRef.current = controlVisible;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Decided per press, not per attach — see the note above.
      if (!controlVisibleRef.current) return;
      if (e.key !== 'l' && e.key !== 'L') return;
      // Any modifier means the reviewer meant a browser/OS shortcut, not this.
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName?.toLowerCase();
        // ScorecardNavigation renders a tenant <select> on this page, and typing
        // into a text field must never cycle the page's language.
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable) {
          return;
        }
      }
      e.preventDefault();
      // Both through refs, never the closure — see the note on selectionRef.
      const current = selectionRef.current;
      setSelectionRef.current({ ...current, mode: nextMode(current.mode) });
    };
    // `[]`, and every value the handler needs reached through a ref. Mount to
    // unmount, with no gap. Adding ANY dependency here re-opens the window.
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const tocBanner = <ViewAsTocLine viewAs={viewAs} empty={viewEmpty} />;

  return (
    <div
      className="min-h-screen bg-cream"
      style={{ '--review-chrome-top': `${chromeTop}px` } as React.CSSProperties}
    >
      <ScorecardNavigation />

      {/* Only Both widens. EN and ES keep `max-w-4xl`, because single-language
          reading is meant to feel like the mobile Learn module and a 1700 px
          single column is ~240 characters per line (design notes §3).
          `pb-28` in EVERY mode: it is what guarantees the last curriculum item
          can always be scrolled clear of the floating pill. */}
      {/* feat-299's providers are OUTERMOST, so the AI-edit layer (the toolbar
          counter included) reaches the one edit coordinator and the edit api. */}
      <ReviewEditContext.Provider value={editApi}>
      <EditCoordinatorProvider>
      <QuestionEditProvider
        tenantId={selectedTenant}
        placements={placements}
        onApplied={onQuestionApplied}
        questionSavedRef={questionSavedRef}
        currentEmail={auth?.user?.email ?? null}
      >
      <AudienceProvider value={audienceApi}>
      <div
        ref={contentRef}
        className={`${containerClasses(both, docked)} mx-auto p-4 md:p-8 pb-28`}
      >
        <header className={`mb-8${docked ? (both ? ' xl:col-span-2' : ' lg:col-span-2') : ''}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-4xl font-display font-semibold text-ink mb-1">
                {COPY.pageTitle}
              </h1>
              <p className="text-sm text-inkMuted">
                {COPY.subtitleLead(grouped?.mode !== 'flat')}{' '}
                <span className="font-semibold text-ink">{tenantName}</span>
                {COPY.subtitleTail}{' '}
                <SubtitleEditHint mode={mode} pending={pending.state} />
              </p>
              <PendingNote pending={pending.state} />
            </div>
            <Link
              to="/quiz-management"
              className="shrink-0 text-sm text-blue-600 hover:text-blue-800 hover:underline mt-2"
            >
              {COPY.editLink}
            </Link>
          </div>
          {navAvailable && (
            <ReviewToolbar
              onExpandAll={expandAll}
              onCollapseAll={collapseAll}
              scopeLabel={AUDIENCE_COPY.viewAs}
              scopeControls={
                audienceOptions.status === 'ready' ? (
                  <ViewAsControls options={audienceOptions.options} viewAs={viewAs} />
                ) : undefined
              }
            >
              <NeedsYouCounter rootRef={contentRef} line={chromeTop + 16} onReveal={revealItem} />
            </ReviewToolbar>
          )}
          {navAvailable && <ViewAsBanner viewAs={viewAs} counts={viewCounts} />}
        </header>

        {docked && (
          <ReviewTocPanel
            visibility={both ? 'hidden xl:flex' : 'hidden lg:flex'}
            onHide={() => setTocHidden(true)}
            nodes={toc}
            activePath={activePath}
            expansion={tocExpansion}
            onJump={jumpTo}
            banner={tocBanner}
          />
        )}

        {!selectedTenant && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
            No tenant selected. Return to a scorecard page and pick a tenant first.
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
            {error}
          </div>
        )}

        {(loading || (content && !tracksReady)) && (
          <div className="p-6 text-sm text-inkMuted">Loading quiz content…</div>
        )}

        {content && grouped && !loading && (
          <main className="min-w-0">
            {/* Column legend — deliberately NOT sticky. A second sticky bar has
                to be offset past ScorecardNavigation, whose height VARIES with
                wrapping, and this repo has already been bitten by that coupling.
                At depth the reader knows which column is which from the pill and
                from the language itself. Hidden when the pair is stacked. */}
            {mode === 'both' && (
              <div className="hidden lg:grid grid-cols-2 mb-3 text-[11px] font-semibold uppercase tracking-wide text-inkMuted">
                <div className="pr-6">{QUIZ_REVIEW_LANGUAGE_COPY.legendEnglish}</div>
                <div className="pl-6 border-l border-softBorder">
                  {languageLabel(selection.language)}
                </div>
              </div>
            )}
            {content.lessons.length > 0 && (
              <SectionControlsContext.Provider value={sectionControls}>
                <SrOpenHint />
                {viewEmpty ? (
                  <ViewAsEmpty viewAs={viewAs} />
                ) : (
                  <ReviewBody grouped={grouped} layout={layout} />
                )}
              </SectionControlsContext.Provider>
            )}
            {content.lessons.length === 0 && (
              <div className="text-sm text-inkMuted italic">
                {COPY.emptyCurriculum}
              </div>
            )}
          </main>
        )}
      </div>
      </AudienceProvider>
      </QuestionEditProvider>
      </EditCoordinatorProvider>
      </ReviewEditContext.Provider>

      {/* Zero configured languages ⇒ NOTHING is rendered. Not a disabled pill,
          not a one-segment pill — the page must be indistinguishable from what
          it was before this feature (AC9). Same for loading/error/no-tenant,
          where the language list is not yet known and a control that appears and
          then vanishes is worse than none (design notes §7). */}
      {navAvailable && (
        <ContentsPill
          ref={pillRef}
          open={!!tocOverlay}
          visibility={
            tocHidden ? 'inline-flex' : both ? 'inline-flex xl:hidden' : 'inline-flex lg:hidden'
          }
          onClick={onContentsPill}
          label={viewAs.active ? AUDIENCE_COPY.contentsFiltered : undefined}
        />
      )}
      {navAvailable && tocOverlay === 'sheet' && (
        <ReviewTocSheet
          onClose={dismissOverlay}
          nodes={toc}
          activePath={activePath}
          expansion={tocExpansion}
          onJump={jumpFromOverlay}
          banner={tocBanner}
        />
      )}
      {navAvailable && tocOverlay === 'popover' && (
        <ReviewTocPopover
          onClose={dismissOverlay}
          pillRef={pillRef}
          nodes={toc}
          activePath={activePath}
          expansion={tocExpansion}
          onJump={jumpFromOverlay}
          banner={tocBanner}
        />
      )}

      {controlVisible && languages && (
        <QuizReviewLanguageControl
          selection={selection}
          languages={languages}
          onChange={setSelection}
        />
      )}
    </div>
  );
};

export default QuizContentReview;
